import React, { useState } from 'react';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InfoTooltip from '../../info-tooltip/info-tooltip';
import { isHeaderOnlyConsiderationType } from '../../../slices/marketplace/pricing/models/type-of-consideration.enum';
import {
    ConsiderationAmounts,
    ConsiderationLedger as ConsiderationLedgerModel,
    ConsiderationLedgerRow,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import {
    EM_DASH,
    considerationTreatmentName,
    considerationTypeName,
    formatMoney,
} from '../../../utils/flowOfFundsFormat';
import { ConsiderationDetailTables } from './ConsiderationDetailTables';
import '../../../containers/ConnectMarketplaceDashboard/LoiComparisonTab/LoiComparisonSummary/LoiComparisonSummary.scss';
import './ConsiderationLedger.scss';

type AmountKey = keyof ConsiderationAmounts;

const FEE_BREAKDOWN_TOOLTIP = 'Fee breakdown available';
const PERCENT_OF_TOTAL_LABEL = '% of Total';

const TAIL_COLUMNS: { key: AmountKey; header: string }[] = [
    { key: 'projectedCashFlow', header: 'Projected Cash Flow' },
    { key: 'projectedFees', header: 'Projected Fees' },
    { key: 'netOfFees', header: 'Net of Fees' },
    { key: 'liabilityPayoffs', header: 'Liability Payoffs' },
    { key: 'employeePortion', header: 'Employee Portion' },
    { key: 'netToOwner', header: 'Net to Owner' },
];

const formatPercentOfTotal = (
    value: number | null | undefined,
    total: number | null | undefined
): string => {
    if (value == null || total == null || total === 0) {
        return EM_DASH;
    }
    const percentOfTotal = (value / total) * 100;
    return `${percentOfTotal.toFixed(1).replace(/\.0$/, '')}%`;
};

interface AmountCellProps {
    value: number | null;
    feesNotApplicable?: boolean;
    tooltipId?: string;
}

const AmountCell = ({ value, feesNotApplicable, tooltipId }: AmountCellProps) => {
    const text = feesNotApplicable ? 'NA' : formatMoney(value);
    const isMuted = text === EM_DASH || text === 'NA';

    return (
        <td className={`cell-value${isMuted ? ' ff-ledger__num--muted' : ''}`}>
            <span>{text}</span>
            {tooltipId && <InfoTooltip text={FEE_BREAKDOWN_TOOLTIP} idText={tooltipId} />}
        </td>
    );
};

const PercentCell = ({
    visible,
    value,
    total,
}: {
    visible: boolean;
    value: number | null;
    total: number | null;
}) =>
    visible ? (
        <td className="cell-value">{formatPercentOfTotal(value, total)}</td>
    ) : (
        <td className="percent-expander-col" />
    );

const PercentToggleHeader = ({
    pctKey,
    visible,
    onToggle,
}: {
    pctKey: string;
    visible: boolean;
    onToggle: (key: string) => void;
}) =>
    visible ? (
        <th className="sub-col-header">
            <button
                type="button"
                className="percent-toggle-btn"
                onClick={() => onToggle(pctKey)}
                aria-label="Collapse % column">
                <FontAwesomeIcon icon={faMinus} />
            </button>
            {PERCENT_OF_TOTAL_LABEL}
        </th>
    ) : (
        <th className="percent-expander-col">
            <button
                type="button"
                className="percent-toggle-btn"
                onClick={() => onToggle(pctKey)}
                aria-label="Expand % column">
                <FontAwesomeIcon icon={faPlus} />
            </button>
        </th>
    );

interface LedgerDataRowProps {
    row: ConsiderationAmounts;
    total: ConsiderationAmounts;
    showFacePercent: boolean;
    showFairPercent: boolean;
    feesNotApplicable?: boolean;
    hasFeeBreakdown?: boolean;
    tooltipId: string;
    timing: React.ReactNode;
    isTotal?: boolean;
}

const LedgerDataRow = ({
    row,
    total,
    showFacePercent,
    showFairPercent,
    feesNotApplicable,
    hasFeeBreakdown,
    tooltipId,
    timing,
    isTotal,
}: LedgerDataRowProps) => (
    <tr className={isTotal ? 'total-row' : undefined}>
        <AmountCell value={row.faceValue} />
        <PercentCell visible={showFacePercent} value={row.faceValue} total={total.faceValue} />
        <AmountCell value={row.fairValue} />
        <PercentCell visible={showFairPercent} value={row.fairValue} total={total.fairValue} />
        <AmountCell value={row.projectedCashFlow} />
        <AmountCell
            value={row.projectedFees}
            feesNotApplicable={feesNotApplicable}
            tooltipId={hasFeeBreakdown ? tooltipId : undefined}
        />
        <AmountCell value={row.netOfFees} />
        <AmountCell value={row.liabilityPayoffs} />
        <AmountCell value={row.employeePortion} />
        <AmountCell value={row.netToOwner} />
        <td className="cell-value">{timing}</td>
    </tr>
);

interface ConsiderationLedgerProps {
    consideration: ConsiderationLedgerModel;
}

export const ConsiderationLedger = ({ consideration }: ConsiderationLedgerProps) => {
    const { rows, total, detail } = consideration;
    const [collapsibleColumns, setCollapsibleColumns] = useState<Record<string, boolean>>({
        face: false,
        fair: false,
    });

    const toggleColumn = (key: string) =>
        setCollapsibleColumns((current) => ({ ...current, [key]: !current[key] }));

    const rowLabel = (row: ConsiderationLedgerRow) => considerationTypeName(row.considerationType);

    return (
        <div className="ff-ledger">
            <div className="loi-comparison-summary">
                <div className="summary-inner">
                    <div className="loi-labels-column">
                        <table>
                            <thead>
                                <tr className="sub-col-header-row">
                                    <th className="col-label section-title">
                                        Consideration Summary
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.considerationType}>
                                        <td
                                            className={`col-label${
                                                row.isSubRow ? ' col-label--indented' : ''
                                            }`}>
                                            {rowLabel(row)}
                                        </td>
                                    </tr>
                                ))}
                                <tr className="total-row">
                                    <td className="col-label">Total Value</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <table className="ff-ledger__data-table">
                        <thead>
                            <tr className="sub-col-header-row">
                                <th className="sub-col-header">Face Values</th>
                                <PercentToggleHeader
                                    pctKey="face"
                                    visible={collapsibleColumns.face}
                                    onToggle={toggleColumn}
                                />
                                <th className="sub-col-header">Fair Value</th>
                                <PercentToggleHeader
                                    pctKey="fair"
                                    visible={collapsibleColumns.fair}
                                    onToggle={toggleColumn}
                                />
                                {TAIL_COLUMNS.map((column) => (
                                    <th key={column.key} className="sub-col-header">
                                        {column.header}
                                    </th>
                                ))}
                                <th className="sub-col-header">Fee Payment Timing</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const isHeaderOnly = isHeaderOnlyConsiderationType(
                                    row.considerationType
                                );
                                return (
                                    <LedgerDataRow
                                        key={row.considerationType}
                                        row={row}
                                        total={total}
                                        showFacePercent={collapsibleColumns.face}
                                        showFairPercent={collapsibleColumns.fair}
                                        feesNotApplicable={row.feesNotApplicable}
                                        hasFeeBreakdown={row.hasFeeBreakdown}
                                        tooltipId={`ff-ledger-fee-tooltip-${row.considerationType}`}
                                        timing={
                                            isHeaderOnly
                                                ? ''
                                                : considerationTreatmentName(row.feePaymentTiming)
                                        }
                                    />
                                );
                            })}
                            <LedgerDataRow
                                row={total}
                                total={total}
                                showFacePercent={collapsibleColumns.face}
                                showFairPercent={collapsibleColumns.fair}
                                hasFeeBreakdown
                                tooltipId="ff-ledger-fee-tooltip-total"
                                timing=""
                                isTotal
                            />
                        </tbody>
                    </table>
                </div>
            </div>

            <ConsiderationDetailTables detail={detail} />
        </div>
    );
};
